'use client';

import { useEffect, useState, useCallback } from 'react';
import Link from 'next/link';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import { useT } from '@/lib/useT';
import {
  Truck, Building2, MapPin, Phone, Users, Search,
  ChevronLeft, ChevronRight,
} from 'lucide-react';

/* ─── types ─────────────────────────────────────────── */
interface Company {
  id: string;
  name: string;
  slug: string;
  country: string;
  city: string | null;
  phone: string | null;
  brandColor: string | null;
  plan: 'FREE' | 'STARTER' | 'BUSINESS' | 'ENTERPRISE';
  _count: { members: number; vehicles: number };
}

interface CompaniesResponse {
  data: Company[];
  countries: string[];
  total: number;
  page: number;
  limit: number;
}

/* ─── helpers ───────────────────────────────────────── */
const FLAGS: Record<string, string> = {
  DE: '\u{1F1E9}\u{1F1EA}', AT: '\u{1F1E6}\u{1F1F9}', CH: '\u{1F1E8}\u{1F1ED}',
  PL: '\u{1F1F5}\u{1F1F1}', CZ: '\u{1F1E8}\u{1F1FF}', UA: '\u{1F1FA}\u{1F1E6}',
  RU: '\u{1F1F7}\u{1F1FA}', MD: '\u{1F1F2}\u{1F1E9}', BG: '\u{1F1E7}\u{1F1EC}',
  KZ: '\u{1F1F0}\u{1F1FF}',
};

const COUNTRY_NAMES: Record<string, string> = {
  DE: 'Deutschland', AT: 'Oesterreich', CH: 'Schweiz', PL: 'Polen',
  CZ: 'Tschechien', UA: 'Ukraine', RU: 'Russland', MD: 'Moldawien',
  BG: 'Bulgarien', KZ: 'Kasachstan',
};

const PLAN_STYLES: Record<string, { bg: string; text: string }> = {
  FREE:       { bg: 'rgba(107,112,132,0.15)', text: '#6B7084' },
  STARTER:    { bg: 'color-mix(in srgb, var(--accent) 15%, transparent)',  text: 'var(--accent)' },
  BUSINESS:   { bg: 'color-mix(in srgb, var(--accent) 15%, transparent)',  text: 'var(--accent)' },
  ENTERPRISE: { bg: 'color-mix(in srgb, var(--live) 15%, transparent)',   text: 'var(--live)' },
};

// Remove /api suffix since we add /api/public manually
const RAW_API = process.env.NEXT_PUBLIC_API_URL || '';
const API = RAW_API.replace(/\/api$/, '');

/* ─── component ─────────────────────────────────────── */
export default function CompaniesPage() {
  const t = useT();
  const [data, setData] = useState<CompaniesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [country, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [page, setPage] = useState(1);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.set('q', search);
      if (country) params.set('country', country);
      if (city) params.set('city', city);
      params.set('page', String(page));

      const res = await fetch(`${API}/api/public/companies?${params.toString()}`);
      if (res.ok) {
        const json = await res.json();
        setData(json);
      }
    } catch {
      // silent
    } finally {
      setLoading(false);
    }
  }, [search, country, city, page]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // Debounced search
  const [searchInput, setSearchInput] = useState('');
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const companies = data?.data ?? [];
  const countries = data?.countries ?? [];
  const totalPages = data ? Math.ceil(data.total / data.limit) : 1;

  return (
    <div className="min-h-screen" style={{ background: 'var(--c-bg)' }}>

      <PublicHeader />

      {/* ════════════ PAGE TITLE ════════════ */}
      <section className="relative overflow-hidden px-6 md:px-8 pt-20 pb-12 max-w-7xl mx-auto text-center">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, color-mix(in srgb, var(--accent) 6%, transparent) 0%, transparent 70%)' }} />

        <div className="relative z-10 animate-fade-up">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-display font-bold text-ink mb-4">
            {t('companies_title')} <span className="gradient-text">LOGIXA</span>
          </h1>
          <p className="text-base md:text-lg max-w-xl mx-auto" style={{ color: 'var(--c-text)' }}>
            {t('companies_subtitle')}
          </p>
        </div>
      </section>

      {/* ════════════ FILTERS ════════════ */}
      <section className="px-6 md:px-8 pb-8 max-w-7xl mx-auto">
        <div className="glass-card p-4 md:p-6 flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--c-muted)' }} />
            <input
              type="text"
              placeholder={t('companies_search_ph')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm text-ink placeholder:text-[var(--c-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/40"
              style={{ background: 'var(--c-bg)', border: '1px solid var(--c-border)' }}
            />
          </div>

          {/* Country */}
          <select
            value={country}
            onChange={(e) => { setCountry(e.target.value); setPage(1); }}
            className="px-4 py-2.5 rounded-xl text-sm text-ink focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/40 appearance-none cursor-pointer"
            style={{ background: 'var(--c-bg)', border: '1px solid var(--c-border)', minWidth: 180 }}
          >
            <option value="">{t('companies_all_countries')}</option>
            {countries.map((c) => (
              <option key={c} value={c}>{FLAGS[c] ?? ''} {COUNTRY_NAMES[c] ?? c}</option>
            ))}
          </select>

          {/* City */}
          <div className="relative">
            <MapPin size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--c-muted)' }} />
            <input
              type="text"
              placeholder={t('companies_city_ph')}
              value={city}
              onChange={(e) => { setCity(e.target.value); setPage(1); }}
              className="w-full md:w-48 pl-10 pr-4 py-2.5 rounded-xl text-sm text-ink placeholder:text-[var(--c-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]/40"
              style={{ background: 'var(--c-bg)', border: '1px solid var(--c-border)' }}
            />
          </div>
        </div>
      </section>

      {/* ════════════ COMPANY GRID ════════════ */}
      <section className="px-6 md:px-8 pb-12 max-w-7xl mx-auto">
        {loading ? (
          <div className="text-center py-20">
            <div className="inline-block w-8 h-8 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: 'var(--c-accent)', borderTopColor: 'transparent' }} />
            <p className="mt-4 text-sm" style={{ color: 'var(--c-muted)' }}>{t('companies_loading')}</p>
          </div>
        ) : companies.length === 0 ? (
          <div className="text-center py-20">
            <Building2 size={48} className="mx-auto mb-4" style={{ color: 'var(--c-muted)' }} />
            <p className="text-lg font-display font-bold text-ink mb-2">{t('companies_none_found')}</p>
            <p className="text-sm" style={{ color: 'var(--c-muted)' }}>{t('companies_try_other')}</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 stagger-children">
              {companies.map((c) => {
                const planStyle = PLAN_STYLES[c.plan] ?? PLAN_STYLES.FREE;
                return (
                  <Link href={`/company/${c.slug}`} key={c.id} className="logixa-card group block overflow-hidden">
                    {/* Brand color accent top bar */}
                    <div className="h-1" style={{ background: c.brandColor || 'var(--c-accent)' }} />

                    <div className="p-6">
                      {/* Company name + plan badge */}
                      <div className="flex items-start justify-between gap-3 mb-4">
                        <h3 className="text-lg font-bold text-ink font-display leading-tight group-hover:text-[var(--accent)] transition-colors">
                          {c.name}
                        </h3>
                        <span className="flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wide"
                          style={{ background: planStyle.bg, color: planStyle.text }}>
                          {c.plan}
                        </span>
                      </div>

                      {/* Location */}
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-base">{FLAGS[c.country] ?? ''}</span>
                        <span className="text-sm" style={{ color: 'var(--c-text)' }}>
                          {COUNTRY_NAMES[c.country] ?? c.country}
                        </span>
                      </div>

                      {c.city && (
                        <div className="flex items-center gap-2 mb-3">
                          <MapPin size={14} style={{ color: 'var(--c-muted)' }} />
                          <span className="text-sm" style={{ color: 'var(--c-text)' }}>{c.city}</span>
                        </div>
                      )}

                      {c.phone && (
                        <div className="flex items-center gap-2 mb-3">
                          <Phone size={14} style={{ color: 'var(--c-muted)' }} />
                          <span className="text-sm" style={{ color: 'var(--c-text)' }}>{c.phone}</span>
                        </div>
                      )}

                      {/* Stats */}
                      <div className="flex items-center gap-4 pt-4" style={{ borderTop: '1px solid var(--c-border)' }}>
                        <div className="flex items-center gap-1.5">
                          <Users size={14} style={{ color: 'var(--c-accent)' }} />
                          <span className="text-xs font-medium" style={{ color: 'var(--c-muted)' }}>
                            {c._count.members} {t('companies_drivers')}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Truck size={14} style={{ color: 'var(--c-accent)' }} />
                          <span className="text-xs font-medium" style={{ color: 'var(--c-muted)' }}>
                            {c._count.vehicles} {t('companies_vehicles')}
                          </span>
                        </div>
                      </div>

                      {/* CTA */}
                      <div className="mt-4 text-sm font-semibold transition-colors group-hover:text-[var(--accent)]" style={{ color: 'var(--c-accent)' }}>
                        {t('companies_view_profile')} &rarr;
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>

            {/* ════════════ PAGINATION ════════════ */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="btn-ghost flex items-center gap-1 text-sm px-4 py-2 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ChevronLeft size={16} /> {t('companies_prev')}
                </button>

                <div className="flex items-center gap-1 mx-4">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                    .reduce<(number | 'dots')[]>((acc, p, idx, arr) => {
                      if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push('dots');
                      acc.push(p);
                      return acc;
                    }, [])
                    .map((p, idx) =>
                      p === 'dots' ? (
                        <span key={`dots-${idx}`} className="px-2 text-sm" style={{ color: 'var(--c-muted)' }}>...</span>
                      ) : (
                        <button
                          key={p}
                          onClick={() => setPage(p as number)}
                          className="w-9 h-9 rounded-lg text-sm font-medium transition-all"
                          style={{
                            background: page === p ? 'var(--c-accent)' : 'transparent',
                            color: page === p ? 'white' : 'var(--c-muted)',
                            border: page === p ? 'none' : '1px solid var(--c-border)',
                          }}
                        >
                          {p}
                        </button>
                      )
                    )}
                </div>

                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page >= totalPages}
                  className="btn-ghost flex items-center gap-1 text-sm px-4 py-2 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  {t('companies_next')} <ChevronRight size={16} />
                </button>
              </div>
            )}
          </>
        )}
      </section>

      <PublicFooter />
    </div>
  );
}
